import { useState, useEffect } from "react";
import axios from "axios";
import BoxBlock from "@/components/ui/BoxBlock";
import ToastContainer from "@/components/ui/ToastContainer";
import { X, Mail, User, Loader2 } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";

export default function NewsletterSubscribeModal({
  open,
  onClose,
  eventTitle, // <- ide jön a coming soon event neve
  initialEmail = "",
}) {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Toast state + helpers
  const [toasts, setToasts] = useState([]);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((tt) => tt.id !== id));
  };

  const pushToast = (type, title, message, opts = {}) => {
    const id = opts.id ?? `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const timeoutMs = opts.timeoutMs ?? 3000;

    setToasts((prev) => [...prev, { id, type, title, message }]);

    if (timeoutMs !== null) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, timeoutMs);
    }

    return id;
  };

  const handleBackendToasts = (json) => {
    if (Array.isArray(json?.toasts)) {
      for (const tObj of json.toasts) {
        pushToast(tObj.type, t(`toast.${tObj.type}`), t(tObj.translationKey, tObj.params));
      }
      return true;
    } else if (json?.toast) {
      pushToast(json.toast.type, json.toast.title, json.toast.message);
      return true;
    }
    return false;
  };

  // ha bezáródik a modal, optional: reset
  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      // itt dönthetsz: reseteljen-e a form vagy ne
      // setEmail(""); setName(""); setConsentGiven(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // ✅ prefillezés: csak ha van initialEmail és a modal input még üres
    if (initialEmail && !email) {
      setEmail(initialEmail);
    }
  }, [open, initialEmail]); // szándékosan nincs benne az `email`, hogy ne loopoljon / ne írja felül gépelés közben


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentGiven) {
      pushToast(
        "error",
        t("toast.error"),
        t("newsletter.consent_required") ?? "Kérlek fogadd el az adatkezelést!"
      );
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    const loadingToastId = pushToast(
      "info",
      t("toast.info"),
      t("newsletter.please_wait") ?? "Kis türelmet, feldolgozzuk a feliratkozást…",
      { timeoutMs: null }
    );

    try {
      const res = await axios.post(`/api/newsletter-subscribers/subscribe`, {
        data: {
          email,
          name,
          consentGiven,
          category: eventTitle || "Általános", // ✅ itt az esemény neve megy be
        },
      });

      removeToast(loadingToastId);

      const hadBackendToast = handleBackendToasts(res?.data);
      if (!hadBackendToast) {
        pushToast("success", t("toast.success"), t("newsletter.success") ?? "Sikeres feliratkozás!");
      }

      setEmail("");
      setName("");
      setConsentGiven(false);
      onClose?.();
    } catch (error) {
      removeToast(loadingToastId);

      const backendJson = error?.response?.data;
      const hadBackendToast = handleBackendToasts(backendJson);
      if (!hadBackendToast) {
        pushToast("error", t("toast.error"), t("newsletter.error") ?? "Hiba történt a feliratkozás során.");
      }

      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* toast mindig legyen, modal fölé is */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={onClose}
      >
        <div className="w-full md:max-w-lg" onClick={(e) => e.stopPropagation()}>
          <BoxBlock
            title={t("home.info.section.join.title2") ?? "Csatlakozz!"}
            icon={<X />}
            iconOnClick={onClose}
            className="divide-y divide-gray-100 dark:divide-zinc-700"
          >
            <div className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
              {/* opcionálisan kiírhatod mire iratkozik */}
              <div className="mb-2 font-medium text-gray-800 dark:text-gray-100">
                {eventTitle ? `Értesítést kérek erről: ${eventTitle}` : null}
              </div>

              {t("home.info.section.modal.text") ??
                "Add meg az e-mail címed, és elsőként értesítünk az új programokról és akciókról!"}
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-4 space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("newsletter.email") ?? "Email cím"}
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="hello@..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("newsletter.name") ?? "Név (opcionális)"}
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    placeholder={t("newsletter.name.placeholder") ?? "Pl. Bence"}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50/70 dark:bg-zinc-900/40 px-3 py-3">
                <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    id="consentGiven"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    Elfogadom az{" "}
                    <a
                      href="/pages/adatkezelesi-tajekoztato"
                      target="_blank"
                      rel="noreferrer"
                      className="underline underline-offset-2"
                    >
                      adatkezelési tájékoztatót
                    </a>
                    .
                  </span>
                </label>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2 rounded-xl border border-gray-300 dark:border-zinc-700
                    bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200
                    hover:bg-gray-50 dark:hover:bg-zinc-800 transition cursor-pointer"
                >
                  Mégse
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-2 rounded-xl font-semibold text-white transition cursor-pointer
                    ${submitting ? "opacity-60 cursor-not-allowed bg-[#fe64db]" : "bg-[#fe64db] hover:opacity-90"}`}
                >
                  {submitting && (
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2 align-[-2px]" />
                  )}
                  {submitting
                    ? (t("newsletter.submitting") ?? "Feliratkozás…")
                    : "Feliratkozás"}
                </button>
              </div>
            </form>
          </BoxBlock>
        </div>
      </div>
    </>
  );
}
